import { Button, ConfirmProvider, Spinner, ToastProvider } from "@templeos/ui";
import { Component, type ReactNode } from "react";
import { Navigate, Outlet, Route, Routes } from "react-router";
import { PrintArea } from "./components/PrintArea.js";
import { ChangePassword, LockScreen, Login, OpenShift, WrongCounter } from "./screens/Access.js";
import { Board } from "./screens/Board.js";
import { CustomerDisplay } from "./screens/CustomerDisplay.js";
import { Fast } from "./screens/Fast.js";
import { Gate } from "./screens/Gate.js";
import { History } from "./screens/History.js";
import { Prasad } from "./screens/Prasad.js";
import { Sell } from "./screens/Sell.js";
import { ShiftClose } from "./screens/ShiftClose.js";
import { SessionProvider, useSession } from "./session.js";

const Loading = () => <div className="pos-center"><Spinner label="Starting the counter" /></div>;

/** Remove every bill being built on this terminal (localStorage drafts); saved bills in IndexedDB are untouched. */
function clearDrafts(): void {
  try {
    for (const k of Object.keys(localStorage)) if (k.startsWith("tos-pos-cart:")) localStorage.removeItem(k);
  } catch { /* storage blocked: nothing to clear */ }
}

/**
 * Last line of defence for the kiosk: an unexpected error shows a way out instead of a blank screen. Saved bills live
 * in IndexedDB and are never touched here; only the bill being built can be discarded.
 */
class CrashScreen extends Component<{ children: ReactNode }, { error: Error | null }> {
  override state = { error: null as Error | null };
  static getDerivedStateFromError(error: Error) { return { error }; }
  override componentDidCatch(error: Error) { console.error("Counter screen failed", error); }
  override render() {
    if (!this.state.error) return this.props.children;
    return (
      <div className="pos-center">
        <div className="tos-card tos-card-body tos-stack" style={{ width: "min(460px, 100%)" }}>
          <div role="alert" style={{ fontWeight: 700 }}>The counter screen hit an unexpected problem. Saved bills are safe on this terminal.</div>
          <div className="tos-muted">{this.state.error.message}</div>
          <Button variant="primary" onClick={() => location.reload()}>Reload the counter</Button>
          <Button onClick={() => { clearDrafts(); location.reload(); }}>Discard the unsaved bill and reload</Button>
        </div>
      </div>
    );
  }
}

function Blocked({ children }: { children: ReactNode }) {
  const { logout } = useSession();
  return (
    <div className="pos-center">
      <div className="tos-card tos-card-body tos-stack" style={{ width: "min(460px, 100%)" }}>
        <div role="alert" style={{ fontWeight: 700 }}>{children}</div>
        <Button variant="primary" onClick={() => void logout()}>Sign out</Button>
      </div>
    </div>
  );
}

/** Signed in, unlocked, and (for billing screens) an open shift at this counter. */
function Frame() {
  const s = useSession();
  if (s.status === "loading") return <Loading />;
  if (s.status === "signedOut") return <Login />;
  if (s.me?.mustChangePassword) return <ChangePassword />;
  return (
    <>
      <div inert={s.locked}><Outlet /></div>
      {s.locked && <LockScreen />}
      <PrintArea />
    </>
  );
}

function Selling({ children }: { children: ReactNode }) {
  const s = useSession();
  if (s.me?.counter?.kind === "gate") return <Navigate to="/gate" replace />;
  if (!s.me?.counter) return <Blocked>Sign in with a counter code to bill.</Blocked>;
  if (!s.can("counters.sell")) return <Blocked>Your role is not allowed to bill at a counter. Ask the administrator for counter access.</Blocked>;
  if (s.shift === undefined) return <Loading />;
  if (s.shift === null) return <OpenShift />;
  if (s.shift.shift.counter_id !== s.me.counter.id) return <WrongCounter />;
  return <>{children}</>;
}

function Home() {
  const { me } = useSession();
  const kind = me?.counter?.kind;
  return <Navigate to={kind === "gate" ? "/gate" : kind === "prasad" ? "/prasad" : "/sell"} replace />;
}

function GateOnly() {
  const s = useSession();
  if (!s.can("counters.gate")) return <Blocked>Your role is not allowed to operate a gate.</Blocked>;
  return <Gate />;
}

export function App() {
  return (
    <Routes>
      <Route path="/board" element={<Board />} />
      <Route path="/customer-display" element={<CustomerDisplay />} />
      <Route element={<ToastProvider><ConfirmProvider><SessionProvider><CrashScreen><Frame /></CrashScreen></SessionProvider></ConfirmProvider></ToastProvider>}>
        <Route path="/gate" element={<GateOnly />} />
        <Route path="/sell" element={<Selling><Sell /></Selling>} />
        <Route path="/fast" element={<Selling><Fast /></Selling>} />
        <Route path="/prasad" element={<Selling><Prasad /></Selling>} />
        <Route path="/history" element={<Selling><History /></Selling>} />
        <Route path="/shift" element={<ShiftOnly />} />
        <Route path="*" element={<Home />} />
      </Route>
    </Routes>
  );
}

function ShiftOnly() {
  const { shift, me } = useSession();
  if (me?.counter?.kind === "gate") return <Navigate to="/gate" replace />;
  if (shift === undefined) return <Loading />;
  return <ShiftClose />;
}
